import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import DataTable from "../components/common/DataTable";
import PageHeader from "../components/common/PageHeader";
import LocationMapView from "../components/common/LocationMapView";
import GooglePlacesDataSpreadsheetView from "../components/GooglePlacesDataSpreadsheetView";
import LocationHistoryModal from "../components/LocationHistoryModal";
import api from "../services/api";
import { showToast, confirmDelete } from "../helper/notifications";
import { useDebounce } from "../hook/useDebounce";
import { Icon } from "@iconify/react";

const LocationsGooglePlacesDataPage = () => {
  const navigate = useNavigate();

  const [data, setData] = useState([]);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);

  // View Mode: 'table', 'sheet', or 'map'
  const [viewMode, setViewMode] = useState("table");

  // Version History Modal State
  const [historyModalOpen, setHistoryModalOpen] = useState(false);
  const [historyLocation, setHistoryLocation] = useState(null);

  /* ===== Sorting State ===== */
  const [sortConfig, setSortConfig] = useState({ key: "updated_at", direction: "desc" });
  const debouncedSearch = useDebounce(search, 500);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const isFullView = viewMode === "map" || viewMode === "sheet";
      const res = await api.get("/google-places-data", {
        params: {
          search: debouncedSearch,
          page: isFullView ? 1 : page,
          limit: isFullView ? 250 : limit,
          sort_by: sortConfig.key,
          order: sortConfig.direction,
        },
      });
      setData(res.data?.data || []);
      setTotal(res.data?.pagination?.total || 0);
    } catch (error) {
      showToast("error", "Failed to load Google Places records");
    } finally {
      setLoading(false);
    }
  }, [debouncedSearch, page, limit, sortConfig, viewMode]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSyncFromMaster = async () => {
    setSyncing(true);
    try {
      const res = await api.post("/google-places-data/sync-from-master");
      if (res.data?.success) {
        showToast(
          "success",
          `Synchronized ${res.data.syncedCount} of ${res.data.totalFound} locations from Google Places API!`
        );
        fetchData();
      } else {
        showToast("error", res.data?.error || "Sync completed with warnings");
      }
    } catch (error) {
      showToast("error", error.response?.data?.error || "Failed to sync from Master Data.");
    } finally {
      setSyncing(false);
    }
  };

  const handleRefreshSingle = async (row) => {
    try {
      showToast("info", `Refreshing "${row.title}" from Google Places...`);
      const res = await api.post(`/google-places-data/sync-single/${row.id}`);
      if (res.data?.success) {
        showToast("success", "Place data refreshed successfully");
        fetchData();
      }
    } catch (error) {
      showToast("error", "Failed to refresh place details.");
    }
  };

  const columns = useMemo(() => [
    {
      key: "title",
      label: "Name",
      category: "General",
      defaultVisible: true,
      className: "text-xs fw-semibold",
      sortable: true,
      render: (row) => (
        <span
          className="text-primary-600 pointer hover-text-decoration-underline"
          onClick={() => navigate(`/google-places-data/${row.id}/view`)}
        >
          {row.title || row.store_code || "Unnamed Place"}
        </span>
      ),
    },
    {
      key: "store_code",
      label: "Store Code",
      category: "General",
      defaultVisible: true,
      className: "text-xs text-secondary-light fw-medium",
      sortable: true,
      render: (row) => row.store_code || "—",
    },
    {
      key: "primary_category_name",
      label: "Category",
      category: "General",
      defaultVisible: false,
      className: "text-xs text-secondary-light fw-medium",
      sortable: true,
      render: (row) => row.primary_category_name || "—",
    },
    {
      key: "city",
      label: "City",
      category: "Address",
      defaultVisible: true,
      className: "text-xs text-secondary-light fw-medium",
      sortable: true,
      render: (row) => row.city || "—",
    },
    {
      key: "street_address",
      label: "Street Address",
      category: "Address",
      defaultVisible: false,
      className: "text-xs text-secondary-light fw-medium",
      sortable: true,
      render: (row) => row.street_address || "—",
    },
    {
      key: "postal_code",
      label: "Postal Code",
      category: "Address",
      defaultVisible: false,
      className: "text-xs text-secondary-light fw-medium",
      sortable: true,
      render: (row) => row.postal_code || "—",
    },
    {
      key: "rating",
      label: "Rating",
      category: "Reputation",
      defaultVisible: true,
      className: "text-xs text-secondary-light fw-medium",
      sortable: true,
      render: (row) => (
        row.rating ? (
          <span className="d-flex align-items-center gap-1">
            <Icon icon="solar:star-bold" className="text-warning-main text-sm" />
            <span className="fw-semibold text-xs">{row.rating}</span>
            <span className="text-2xs text-secondary-light">({row.user_ratings_total || 0})</span>
          </span>
        ) : "—"
      ),
    },
    {
      key: "user_ratings_total",
      label: "Reviews Count",
      category: "Reputation",
      defaultVisible: false,
      className: "text-xs text-secondary-light fw-medium",
      sortable: true,
      render: (row) => row.user_ratings_total ?? "—",
    },
    {
      key: "open_status",
      label: "Status",
      category: "General",
      defaultVisible: true,
      sortable: true,
      render: (row) => {
        const status = row.open_status || row.business_status || "OPEN";
        const isOk = status === "OPEN" || status === "OPERATIONAL" || status === "SYNCED";

        return (
          <span
            className={`badge px-8 py-2 radius-4 fw-medium text-xs ${
              isOk
                ? "bg-success-focus text-success-main"
                : "bg-warning-focus text-warning-main"
            }`}
          >
            {status}
          </span>
        );
      },
    },
    {
      key: "primary_phone",
      label: "Phone",
      category: "Contact",
      defaultVisible: false,
      className: "text-xs text-secondary-light fw-medium",
      sortable: true,
      render: (row) => row.primary_phone || "—",
    },
    {
      key: "website_uri",
      label: "Website",
      category: "Contact",
      defaultVisible: false,
      className: "text-xs text-secondary-light fw-medium",
      sortable: true,
      render: (row) => row.website_uri ? (
        <a href={row.website_uri} target="_blank" rel="noreferrer" className="text-primary-600 text-decoration-none text-xs">
          Link ↗
        </a>
      ) : "—",
    },
    {
      key: "google_place_id",
      label: "Google Place ID",
      category: "System",
      defaultVisible: false,
      className: "text-xs text-secondary-light fw-medium",
      sortable: false,
      render: (row) => row.google_place_id ? `${row.google_place_id.slice(0, 16)}...` : "—",
    },
    {
      key: "wheelchair_accessible_entrance",
      label: "Wheelchair Entrance",
      category: "Accessibility",
      defaultVisible: false,
      sortable: false,
      render: (row) => (
        row.wheelchair_accessible_entrance === 1 || row.wheelchair_accessible_entrance === true ? (
          <span className="badge bg-success-focus text-success-main text-xs px-8 py-2 radius-4">Yes</span>
        ) : row.wheelchair_accessible_entrance === 0 || row.wheelchair_accessible_entrance === false ? (
          <span className="badge bg-neutral-100 text-secondary-light text-xs px-8 py-2 radius-4">No</span>
        ) : "—"
      ),
    },
    {
      key: "version_number",
      label: "Version",
      category: "System",
      defaultVisible: false,
      className: "text-xs text-secondary-light fw-medium",
      sortable: true,
      render: (row) => (
        <span className="badge bg-neutral-100 text-neutral-700 text-2xs px-8 py-4 radius-6">
          v{row.version_number || 1}
        </span>
      ),
    },
    {
      key: "last_synced_at",
      label: "Last Synced",
      category: "System",
      defaultVisible: false,
      className: "text-xs text-secondary-light fw-medium",
      sortable: true,
      render: (row) => row.last_synced_at ? new Date(row.last_synced_at).toLocaleString("en-US", { dateStyle: "short", timeStyle: "short" }) : "—",
    },
    {
      key: "updated_at",
      label: "Last Modified",
      category: "System",
      defaultVisible: false,
      className: "text-xs text-secondary-light fw-medium",
      sortable: true,
      render: (row) => row.updated_at ? new Date(row.updated_at).toLocaleString("en-US", { dateStyle: "short", timeStyle: "short" }) : "—",
    },
  ], [navigate]);

  return (
    <>
      <PageHeader
        title="Google Places Data"
        subtitle="Manage and synchronize Google Places data loaded from Master Data."
        actions={
          <div className="btn-group radius-8 overflow-hidden border">
            <button
              className={`btn btn-sm ${viewMode === "table" ? "btn-primary" : "btn-light text-neutral-600"}`}
              onClick={() => setViewMode("table")}
            >
              <Icon icon="ri:table-line" className="me-1 text-base" /> Table
            </button>
            <button
              className={`btn btn-sm ${viewMode === "sheet" ? "btn-primary" : "btn-light text-neutral-600"}`}
              onClick={() => setViewMode("sheet")}
            >
              <Icon icon="ri:file-excel-2-line" className="me-1 text-base" /> Sheet View
            </button>
            <button
              className={`btn btn-sm ${viewMode === "map" ? "btn-primary" : "btn-light text-neutral-600"}`}
              onClick={() => setViewMode("map")}
            >
              <Icon icon="ri:map-pin-line" className="me-1 text-base" /> Map View
            </button>
          </div>
        }
      />

      {viewMode === "table" ? (
        <DataTable
          storageKey="loqation_table_columns_gpd_v1"
          data={data}
          loading={loading}
          searchTerm={search}
          onSearchChange={(v) => {
            setSearch(v);
            setPage(1);
          }}
          page={page}
          limit={limit}
          total={total}
          onPageChange={setPage}
          onLimitChange={(v) => {
            setLimit(v);
            setPage(1);
          }}
          showCheckbox={false}
          sortConfig={sortConfig}
          onSortChange={(config) => {
            setSortConfig(config);
            setPage(1);
          }}
          headerActions={[
            {
              label: syncing ? "Syncing Places..." : "Sync from Master Data",
              icon: syncing ? "line-md:loading-twotone-loop" : "solar:refresh-linear",
              className: "btn btn-outline-primary radius-8 px-20 py-11 d-flex align-items-center gap-2 text-xs",
              onClick: handleSyncFromMaster,
              disabled: syncing,
            },
          ]}
          columns={columns}
          actions={[
            {
              icon: "solar:refresh-linear",
              className: "w-28-px h-28-px bg-info-focus text-info-main rounded d-flex align-items-center justify-content-center border-0 text-xs",
              title: "Refresh from Places API",
              onClick: (row) => handleRefreshSingle(row),
            },
            {
              icon: "lucide:eye",
              className: "w-28-px h-28-px bg-success-focus text-success-main rounded d-flex align-items-center justify-content-center border-0 text-xs",
              title: "View Details",
              onClick: (row) => navigate(`/google-places-data/${row.id}/view`),
            },
            {
              icon: "solar:history-linear",
              className: "w-28-px h-28-px bg-warning-focus text-warning-main rounded d-flex align-items-center justify-content-center border-0 text-xs",
              title: "View Version History",
              onClick: (row) => {
                setHistoryLocation(row);
                setHistoryModalOpen(true);
              },
            },
            {
              icon: "mingcute:delete-2-line",
              className: "w-28-px h-28-px bg-danger-focus text-danger-main rounded d-flex align-items-center justify-content-center border-0 text-xs",
              title: "Delete Record",
              onClick: (row) => {
                confirmDelete(row.title || "Google Place record", async () => {
                  try {
                    await api.delete(`/google-places-data/${row.id}`);
                    showToast("success", "Place record deleted");
                    fetchData();
                  } catch {
                    showToast("error", "Delete failed");
                  }
                });
              },
            },
          ]}
        />
      ) : viewMode === "sheet" ? (
        <GooglePlacesDataSpreadsheetView
          data={data}
          loading={loading}
          onRefresh={fetchData}
          onRefreshSingle={handleRefreshSingle}
          onOpenHistory={(row) => {
            setHistoryLocation(row);
            setHistoryModalOpen(true);
          }}
        />
      ) : (
        <LocationMapView
          locations={data}
          loading={loading}
          allowPinDrag={false}
          onRefresh={fetchData}
          getViewRoute={(item) => `/google-places-data/${item.id}/view`}
        />
      )}

      {/* Version History Modal */}
      {historyModalOpen && historyLocation && (
        <LocationHistoryModal
          isOpen={historyModalOpen}
          onClose={() => {
            setHistoryModalOpen(false);
            setHistoryLocation(null);
          }}
          endpoint={`/google-places-data/${historyLocation.id}/history`}
          onRestore={() => {
            setHistoryModalOpen(false);
            setHistoryLocation(null);
            fetchData();
          }}
        />
      )}
    </>
  );
};

export default LocationsGooglePlacesDataPage;

